import { connectIntegration } from '../../lib/integrationConnect'
import { posthog } from '../../lib/posthog'
import { Button } from '../../components/ui/button'
import { Note } from '../../components/ui/note'
import type { SettingsCardProps } from '../index'
import { DisconnectButton, IntegrationCard } from '../ui'
import { canvaStatusOf } from './api'
import { CanvaTile } from './Tile'

/** Canva's card on the Integrations page: one OAuth connection per user,
 *  drawn on by every canvas import and "Send to Canva". */
export function CanvaSettingsCard({ status, busy, setBusy, showToast, requestDisconnect }: SettingsCardProps) {
  const canva = canvaStatusOf(status)

  /** Leave for Canva's consent screen. */
  async function connect() {
    if (busy) return
    setBusy(true)
    try {
      const destination = await connectIntegration('canva', () =>
        posthog.capture('integration_connect_started', { provider: 'canva' }),
      )
      if (destination === 'browser') setBusy(false)
    } catch (error) {
      console.error(error)
      showToast('Couldn’t start the Canva connection')
      setBusy(false)
    }
  }

  return (
    <IntegrationCard
      tile={<CanvaTile size={36} />}
      name="Canva"
      detail={canva.connected ? (canva.accountName ?? 'Canva') : 'Import Canva designs, send frames back'}
      connected={canva.connected}
      footnote={canva.connected ? 'Import from any canvas' : ''}
      actions={
        canva.connected ? (
          <>
            <Button variant="bare" size="sm" disabled={busy || !canva.enabled} onClick={() => void connect()}>
              Reconnect
            </Button>
            <DisconnectButton disabled={busy} onClick={requestDisconnect} />
          </>
        ) : (
          <Button variant="default" size="sm" disabled={busy || !canva.enabled} onClick={() => void connect()}>
            Connect
          </Button>
        )
      }
    >
      {!canva.enabled && <Note>Not configured on this server — set CANVA_CLIENT_ID and CANVA_CLIENT_SECRET.</Note>}
      {canva.connected && (
        <Note>
          Paste a Canva link into a canvas Import — designs arrive as editable frames (whiteboards and docs as images) —
          or right-click a frame → Send to Canva for a fully editable design.
        </Note>
      )}
    </IntegrationCard>
  )
}
